import { gql } from '@apollo/client'

import { FRAGMENT_CREATOR } from './fragments/creator'

export const GET_TERRITORY_POSTS_SEARCH = gql`
  query getTerritoryPostsSearch(
    $territoryId: ID!
    $pagination: TerritoryPostsPagination! = {}
    $filters: TerritoryPostsSearchFiltersInput
  ) {
    getTerritory(territoryId: $territoryId) {
      territoryId
      territoryPosts {
        search(pagination: $pagination, filters: $filters) {
          cursor
          data {
            postId
            provider
            thumbnail
            title
            views
            likes
            publishedAt
            isPublicity
            creatorId
            creator {
              ...FragmentCreator
              ...FragmentCreatorIncompleteIN
              ...FragmentCreatorIncompleteTT
              ...FragmentCreatorIncompleteYT
              ...FragmentCreatorIncompleteFB
              ...FragmentCreatorIncompleteTW
              ...FragmentCreatorPublic
            }
          }
          total
        }
      }
    }
  }
  ${FRAGMENT_CREATOR}
`

export const GET_TERRITORY_POSTS_PERFORMANCE = gql`
  query GET_TERRITORY_POSTS_PERFORMANCE($territoryId: ID!, $filters: TerritoryVideosPerPeriodFilter!) {
    getTerritory(territoryId: $territoryId) {
      territoryId
      territoryPerformance {
        videosPerPeriod(filters: $filters) {
          sparkline {
            date
            videos
          }
        }
      }
    }
  }
`

export const MANAGE_TERRITORY_FAVORITE_MUTATION = gql`
  mutation favoriteTerritory($territoriesToFollow: [ID!]!, $territoriesToUnFollow: [ID!]!) {
    editManageWorkspaceFollowTerritory(
      territoriesToFollow: $territoriesToFollow
      territoriesToUnFollow: $territoriesToUnFollow
    ) {
      success
    }
  }
`

export const GET_TERRITORY_FOR_TABLE = gql`
  query GET_TERRITORY_FOR_TABLE(
    $territoryId: ID!
    $timeRange: TimeRange
    $providers: [Providers!]
    $ages: [String!]
    $countries: [String!]
    $genders: [String!]
    $shouldGetPosts: Boolean!
    $publishedAt: RangeQuery
    $postsLimit: Int = 3
    $selfContentBrandIds: [ID!]
    $paidBrandIds: [ID!]
    $earnedBrandIds: [ID!]
    $territoryIds: [ID!]
  ) {
    getTerritory(territoryId: $territoryId) {
      territoryId
      isFollowed
      name

      promotionsV2 {
        source {
          territoryId
        }
        target {
          territoryId
          name
        }
      }

      territoryMostEngaged @include(if: $shouldGetPosts) {
        posts(
          limit: $postsLimit
          filters: { providers: $providers, timeRange: $timeRange, publishedAt: $publishedAt }
          territoryContentFilters: {
            selContentBrandIds: $selfContentBrandIds
            paidBrandIds: $paidBrandIds
            earnedBrandIds: $earnedBrandIds
            territoryIds: $territoryIds
          }
        ) {
          postId
          provider
          thumbnail
          title
          creator {
            ...FragmentCreator
            ...FragmentCreatorIncompleteIN
            ...FragmentCreatorIncompleteTT
            ...FragmentCreatorIncompleteYT
            ...FragmentCreatorIncompleteFB
            ...FragmentCreatorIncompleteTW
            ...FragmentCreatorPublic
          }
        }
      }

      territoryPerformance {
        totalPosts(
          timeRange: $timeRange
          publishedAt: $publishedAt
          providers: $providers
          territoryContentFilters: {
            selContentBrandIds: $selfContentBrandIds
            paidBrandIds: $paidBrandIds
            earnedBrandIds: $earnedBrandIds
            territoryIds: $territoryIds
          }
        )
        sampledMetrics(
          filters: {
            timeRange: $timeRange
            ages: $ages
            countries: $countries
            genders: $genders
            publishedAt: $publishedAt
            providers: $providers
          }
          territoryContentFilters: {
            selContentBrandIds: $selfContentBrandIds
            paidBrandIds: $paidBrandIds
            earnedBrandIds: $earnedBrandIds
            territoryIds: $territoryIds
          }
        ) {
          avgEngagement
          avgViews
        }
        metrics(
          timeRange: $timeRange
          publishedAt: $publishedAt
          providers: $providers
          territoryContentFilters: {
            selContentBrandIds: $selfContentBrandIds
            paidBrandIds: $paidBrandIds
            earnedBrandIds: $earnedBrandIds
            territoryIds: $territoryIds
          }
        ) {
          totalEngagement
          totalViews
        }
      }

      ... on Topic {
        language
        category {
          topicGroupId
          title
        }
      }
      ... on Subtopic {
        language
        allParents {
          name
          territoryId
        }
      }
      ... on Niche {
        allParents {
          name
          territoryId
        }
        territoryQuery
      }

      ... on SavedPostSearch {
        filters
      }
    }
  }
  ${FRAGMENT_CREATOR}
`

export const GET_FAVORITE_TERRITORIES_TOTAL = gql`
  query getFavoritesTerritoriesTotal {
    getWorkspace {
      id
      followedTerritories(pagination: { take: 20 }) {
        totalCount
      }
    }
  }
`

export const GET_TERRITORY_AUTOMATIC_DESCRIPTION = gql`
  query getTerritoryAutomaticDescription(
    $id: ID!
    $language: TaxonomyLanguages! = por
    $providers: [Providers!]
    $timeRange: TimeRange
    $publishedAt: RangeQuery
  ) {
    getTerritory(territoryId: $id) {
      name
      territoryId
      territoryDescription {
        automaticallyGenerated(
          filters: { language: $language, providers: $providers, timeRange: $timeRange, publishedAt: $publishedAt }
        )
      }
    }
  }
`

export const GET_PLATFORM_DISTRIBUTION = gql`
  query getPlatformDistribution(
    $id: ID!
    $timeRange: TimeRange
    $publishedAt: RangeQuery
    $territoryContentFilters: TerritoryContentFilters
  ) {
    getTerritory(territoryId: $id) {
      name
      territoryId
      territoryPerformance {
        postDistributionByPlatform(
          filters: { timeRange: $timeRange, publishedAt: $publishedAt }
          territoryContentFilters: $territoryContentFilters
        ) {
          fb
          in
          tt
          tw
          yt
        }
      }
    }
  }
`

export const GET_AUDIENCE_CARD_DATA = gql`
  query GET_AUDIENCE_CARD_DATA(
    $id: ID!
    $providers: [Providers!]
    $timeRange: TimeRange
    $sparklineTimeRange: TimeRange
    $interval: SparklineIntervalInput
    $ages: [String!]
    $genders: [String!]
    $countries: [String!]
    $publishedAt: RangeQuery
    $territoryContentFilters: TerritoryContentFilters
  ) {
    getTerritory(territoryId: $id) {
      territoryId
      territoryPerformance {
        relevancev2(
          filters: {
            timeRange: $sparklineTimeRange
            interval: $interval
            providers: $providers
            publishedAt: $publishedAt
          }
          audience: { ages: $ages, genders: $genders, countries: $countries }
          territoryContentFilters: $territoryContentFilters
        ) {
          sparkline
          startDate
          endDate
        }
        metrics(
          providers: $providers
          timeRange: $timeRange
          ages: $ages
          countries: $countries
          genders: $genders
          publishedAt: $publishedAt
          territoryContentFilters: $territoryContentFilters
        ) {
          totalEngagement
          totalEngagementVariation
          totalViews
          totalViewsVariation
        }
      }
    }
  }
`

export const GET_TERRITORIES_RELEVANCE = gql`
  query GetTerritoriesRelevance(
    $ids: [ID!]!
    $filters: TerritoryRelevanceFilters
    $territoryContentFilters: TerritoryContentFilters
  ) {
    listTerritories(ids: $ids) {
      territoryId
      name
      promotionsV2 {
        source {
          territoryId
        }
        target {
          territoryId
          name
        }
      }
      territoryPerformance {
        relevancev2(filters: $filters, territoryContentFilters: $territoryContentFilters) {
          endDate
          interval
          sparkline
          startDate
        }
      }
    }
  }
`

export const GET_TERRITORIES_VIEWS_OVER_TIME = gql`
  query GetTerritoriesViewsOverTime(
    $ids: [ID!]!
    $timeRange: TimeRange
    $publishedAt: RangeQuery
    $providers: [Providers!]
    $interval: SparklineIntervalInput
    $territoryContentFilters: TerritoryContentFilters
  ) {
    listTerritories(ids: $ids) {
      territoryId
      name
      promotionsV2 {
        source {
          territoryId
        }
        target {
          territoryId
          name
        }
      }
      territoryPerformance {
        metricsPerMonth(
          timeRange: $timeRange
          publishedAt: $publishedAt
          providers: $providers
          interval: $interval
          territoryContentFilters: $territoryContentFilters
        ) {
          totalViews
          date
        }
      }
    }
  }
`

export const GET_TERRITORY_TITLE = gql`
  query getTerritoryTitle($territoryId: ID!) {
    getTerritory(territoryId: $territoryId) {
      territoryId
      name
      promotionsV2 {
        target {
          territoryId
        }
        parent {
          territoryId
        }
        sourceContextId
      }
    }
  }
`

export const GET_SCOPE_TERRITORY = gql`
  query getScopeTerritory($territoryId: ID!) {
    getTerritory(territoryId: $territoryId) {
      territoryId
      name
      promotionsV2 {
        target {
          territoryId
        }
      }
      ... on IndustryTopic {
        territoryQuery
      }
    }
  }
`

export const GET_BRAND_RADAR_POSTS = gql`
  query getBrandRadarPosts(
    $territoryId: ID!
    $timeRange: TimeRange
    $providers: [Providers!]
    $publishedAt: RangeQuery
    $postsLimit: Int = 3
    $territoryContentFilters: TerritoryContentFilters
  ) {
    getTerritory(territoryId: $territoryId) {
      name
      territoryId
      territoryMostEngaged {
        posts(
          limit: $postsLimit
          filters: { providers: $providers, timeRange: $timeRange, publishedAt: $publishedAt }
          territoryContentFilters: $territoryContentFilters
        ) {
          postId
          provider
          thumbnail
          title
          creator {
            ...FragmentCreator
            ...FragmentCreatorIncompleteIN
            ...FragmentCreatorIncompleteTT
            ...FragmentCreatorIncompleteYT
            ...FragmentCreatorIncompleteFB
            ...FragmentCreatorIncompleteTW
            ...FragmentCreatorPublic
          }
        }
      }
    }
  }
  ${FRAGMENT_CREATOR}
`

export const GET_TERRITORY_DESCRIPTION = gql`
  query GET_TERRITORY_DESCRIPTION($territoryId: ID!, $filters: TerritoryDescriptionAutomaticGenerationFilter!) {
    getTerritory(territoryId: $territoryId) {
      territoryId
      territoryDescription {
        automaticallyGenerated(filters: $filters)
      }
    }
  }
`

export const GET_BRAND_OVERVIEW_CARDS_DATA = gql`
  query getBrandOverviewCardsData(
    $id: ID!
    $providers: [Providers!]
    $timeRange: TimeRange
    $interval: SparklineIntervalInput
    $publishedAt: RangeQuery
    $territoryContentFilters: TerritoryContentFilters
    $includeCreators: Boolean!
  ) {
    getTerritory(territoryId: $id) {
      territoryId
      territoryPerformance {
        videosPerPeriod(
          filters: { interval: $interval, providers: $providers, timeRange: $timeRange, publishedAt: $publishedAt }
          territoryContentFilters: $territoryContentFilters
        ) {
          total
        }
        totalCreators(
          timeRange: $timeRange
          providers: $providers
          publishedAt: $publishedAt
          territoryContentFilters: $territoryContentFilters
        ) @include(if: $includeCreators)
      }
      territoryMostEngaged {
        posts(
          filters: { providers: $providers, timeRange: $timeRange, publishedAt: $publishedAt }
          territoryContentFilters: $territoryContentFilters
          limit: 1
        ) {
          postId
          thumbnail
          creator @include(if: $includeCreators) {
            ...FragmentCreator
            ...FragmentCreatorIncompleteIN
            ...FragmentCreatorIncompleteTT
            ...FragmentCreatorIncompleteYT
            ...FragmentCreatorIncompleteFB
            ...FragmentCreatorIncompleteTW
            ...FragmentCreatorPublic
          }
        }
      }
    }
  }
  ${FRAGMENT_CREATOR}
`

export const LIST_TERRITORIES_WITH_METRICS = gql`
  query listTerritoriesWithMetrics(
    $ids: [ID!]!
    $timeRange: TimeRange
    $providers: [Providers!]
    $publishedAt: RangeQuery
  ) {
    listTerritories(ids: $ids) {
      territoryId
      name
      territoryPerformance {
        totalPosts(timeRange: $timeRange, publishedAt: $publishedAt, providers: $providers)
        sampledMetrics(filters: { timeRange: $timeRange, publishedAt: $publishedAt, providers: $providers }) {
          avgEngagement
          avgViews
        }
      }
    }
  }
`

export const GET_OWNED_TOPICS = gql`
  query GET_OWNED_TOPICS($search: SearchSavedPostSearchInput, $pagination: PaginationInput) {
    listSavedPostSearchesPaginated(search: $search, pagination: $pagination) {
      rows
      entities {
        name
        id
        isFollowed
        territoryId
        updatedAt
        isFollowed
        createdBy {
          firstName
        }
        createdAt
        query
        filters
      }
    }
  }
`

export const GET_UNIFIED_TERRITORIES = gql`
  query getUnifiedTerritories(
    $pagination: TerritoryPaginationInput!
    $filters: ListTerritoryFilters!
    $sort: ListTerritoriesSort!
    $timeRange: TimeRange
    $audience: AudienceFiltersInput!
    $ages: [String!]
    $countries: [String!]
    $genders: [String!]
  ) {
    listTerritoriesPaginated(pagination: $pagination, filters: $filters, sort: $sort, audience: $audience) {
      cursor
      totalCount
      data {
        territoryId
        isFollowed
        name
        territoryPerformance {
          totalPosts(timeRange: $timeRange)
          sampledMetrics(filters: { timeRange: $timeRange, ages: $ages, countries: $countries, genders: $genders }) {
            avgEngagement
            avgViews
          }
        }

        ... on Topic {
          language
          category {
            topicGroupId
            title
          }
        }
        ... on Subtopic {
          language
          allParents {
            name
            territoryId
          }
        }
        ... on Niche {
          allParents {
            name
            territoryId
          }
        }

        ... on SavedPostSearch {
          filters
        }
      }
    }
  }
`

export const GET_COUNTRY_THEMES = gql`
  query getCountryThemes($pagination: TerritoryPaginationInput!, $timeRange: TimeRange) {
    listCountryTerritories(pagination: $pagination, types: [theme]) {
      totalCount
      data {
        territoryId
        isFollowed
        name
        territoryPerformance {
          totalPosts(timeRange: $timeRange)
          sampledMetrics(filters: { timeRange: $timeRange }) {
            avgEngagement
            avgViews
          }
        }
      }
    }
  }
`

export const GET_FAVORITE_TERRITORIES = gql`
  query getFavoriteTerritories(
    $pagination: TerritoryPaginationInput!
    $filters: ListFollowedTerritoriesFilters!
    $sort: ListTerritoriesSort!
    $timeRange: TimeRange
    $ages: [String!]
    $countries: [String!]
    $genders: [String!]
  ) {
    listFollowedTerritories(pagination: $pagination, filters: $filters, sort: $sort) {
      cursor
      totalCount
      data {
        territoryId
        isFollowed
        name
        territoryPerformance {
          totalPosts(timeRange: $timeRange)
          sampledMetrics(filters: { timeRange: $timeRange, ages: $ages, countries: $countries, genders: $genders }) {
            avgEngagement
            avgViews
          }
        }

        ... on Topic {
          language
          category {
            topicGroupId
            title
          }
        }
        ... on Subtopic {
          language
          allParents {
            name
            territoryId
          }
        }
        ... on Niche {
          allParents {
            name
            territoryId
          }
        }

        ... on SavedPostSearch {
          filters
        }
      }
    }
  }
`

export const GET_TERRITORY_NICHES_BRANDED_VS_UGC = gql`
  query getTerritoryNichesBrandedVsUgc(
    $territoryId: ID!
    $contextId: ID!
    $selfContentBrandIds: [ID!]
    $paidBrandIds: [ID!]
    $earnedBrandIds: [ID!]
    $territoryIds: [ID!]
    $providers: [Providers!]
    $timeRange: TimeRange
    $publishedAt: RangeQuery
  ) {
    getTerritory(territoryId: $territoryId) {
      territoryId
      territoryNiches {
        territoryNiches(filters: { contextId: $contextId }) {
          data {
            territoryId
            name
            promotionsV2 {
              source {
                territoryId
              }
              target {
                territoryId
                name
              }
            }
            territoryPerformance {
              brandedMetrics: metrics(
                providers: $providers
                timeRange: $timeRange
                publishedAt: $publishedAt
                # The param selContentBrandIds has a typo in the backend, but it is what it is for now, we just need to make sure to pass the self content brand ids through that param
                territoryContentFilters: {
                  selContentBrandIds: $selfContentBrandIds
                  paidBrandIds: $paidBrandIds
                  territoryIds: $territoryIds
                }
              ) {
                totalPosts
                totalEngagement
                totalViews
              }
              brandedSampledMetrics: sampledMetrics(
                filters: { providers: $providers, timeRange: $timeRange, publishedAt: $publishedAt }
                territoryContentFilters: {
                  selContentBrandIds: $selfContentBrandIds
                  paidBrandIds: $paidBrandIds
                  territoryIds: $territoryIds
                }
              ) {
                avgEngagement
                avgViews
              }
              ugcMetrics: metrics(
                providers: $providers
                timeRange: $timeRange
                publishedAt: $publishedAt
                territoryContentFilters: { earnedBrandIds: $earnedBrandIds, territoryIds: $territoryIds }
              ) {
                totalPosts
                totalEngagement
                totalViews
              }
              ugcSampledMetrics: sampledMetrics(
                filters: { providers: $providers, timeRange: $timeRange, publishedAt: $publishedAt }
                territoryContentFilters: { earnedBrandIds: $earnedBrandIds, territoryIds: $territoryIds }
              ) {
                avgEngagement
                avgViews
              }
            }
          }
        }
      }
    }
  }
`

export const GET_BASIC_TERRITORIES_LIST = gql`
  query GetBasicTerritoriesList(
    $pagination: TerritoryPaginationInput!
    $sort: ListTerritoriesSort!
    $filters: ListTerritoryFilters!
  ) {
    listTerritoriesPaginated(pagination: $pagination, sort: $sort, filters: $filters) {
      cursor
      totalCount
      data {
        territoryId
        name
      }
    }
  }
`

export const GET_TERRITORY_SAVED_POST_SEARCH_TOTAL_POST_BY_ID = gql`
  query GetTerritorySavedPostTotalPostById($territoryId: ID!, $timeRange: TimeRange) {
    getTerritory(territoryId: $territoryId) {
      territoryId
      ... on SavedPostSearch {
        territoryPerformance {
          totalPosts(timeRange: $timeRange)
        }
      }
    }
  }
`

export const CREATE_OWNED_TOPIC = gql`
  mutation CREATE_OWNED_TOPIC($savedPostSearch: SavedPostSearchInput!) {
    createSavedPostSearch(savedPostSearch: $savedPostSearch) {
      ... on SavedPostSearch {
        id
        name
        query
        filters
      }
      ... on StatusMessage {
        success
        message
      }
    }
  }
`

export const TERRITORY_PERFORMANCE_TAB_QUERY = gql`
  query TerritoryPerformanceTabQuery(
    $territoryId: ID!
    $timeRange: TimeRange
    $interval: SparklineIntervalInput
    $providers: [Providers!]
    $publishedAt: RangeQuery
    $territoryContentFilters: TerritoryContentFilters
    $isTotalEngagementEnabled: Boolean!
  ) {
    getTerritory(territoryId: $territoryId) {
      territoryId
      territoryPerformance {
        totalPosts(
          timeRange: $timeRange
          providers: $providers
          publishedAt: $publishedAt
          territoryContentFilters: $territoryContentFilters
        )
        cumulativeEngagement(
          timeRange: $timeRange
          interval: $interval
          providers: $providers
          publishedAt: $publishedAt
          territoryContentFilters: $territoryContentFilters
        ) @include(if: $isTotalEngagementEnabled) {
          date
          engagement
        }
        metrics(
          timeRange: $timeRange
          providers: $providers
          publishedAt: $publishedAt
          territoryContentFilters: $territoryContentFilters
        ) {
          totalViews
          totalEngagement
        }
        relevancev2(
          filters: { timeRange: $timeRange, interval: $interval, providers: $providers, publishedAt: $publishedAt }
          territoryContentFilters: $territoryContentFilters
        ) {
          sparkline
          endDate
          startDate
        }
        videosPerMonth(
          timeRange: $timeRange
          interval: $interval
          providers: $providers
          publishedAt: $publishedAt
          territoryContentFilters: $territoryContentFilters
        ) {
          videos
          date
        }
      }
    }
  }
`

export const GET_TERRITORY_CREATORS = gql`
  query GetTerritoryCreators(
    $territoryId: ID!
    $timeRange: TimeRange
    $providers: [Providers!]
    $subscribers: RangeQuery
    $offset: Int!
    $sortBy: TerritoryTopCreatorsSort
    $take: Int!
    $includeWorkspaceBrand: Boolean
    $publishedAt: RangeQuery
    $isBrand: Boolean
  ) {
    getTerritory(territoryId: $territoryId) {
      territoryId
      territoryCreators {
        search(
          filters: {
            isBrand: $isBrand
            timeRange: $timeRange
            providers: $providers
            subscribers: $subscribers
            includeWorkspaceBrand: $includeWorkspaceBrand
            publishedAt: $publishedAt
          }
          pagination: { offset: $offset, sortBy: $sortBy, take: $take }
        ) {
          total
          hasMore
          data {
            id
            provider
            creator {
              _id
              creatorId
              title
              thumbnail
              groupId
              isTrending
              isBrandGroup
              talkingAboutInfo {
                entity {
                  ... on Topic {
                    topicId
                    title
                  }
                  ... on TerritoryBasic {
                    territoryId
                    name
                  }
                  ... on CreatorTalkingAboutTag {
                    title
                  }
                  ... on TopicPublic {
                    topicId
                    title
                  }
                }
              }
            }
            avgEngagement
            avgViews
            subscribers
            isBrand
            country
          }
        }
      }
    }
  }
`
